import { Type } from 'class-transformer';

import { Media } from '@eiq/files/models';

import { FileType } from '@empathy/global/data';

import { CarouselTypeResult } from './carousel-type-result.interface';
import { ChoiceQuestionSettings } from './choice-question-settings.interface';
import { ChoiceTypeResult } from './choice-type-result.interface';
import { ImageFit } from './image-fit.type';
import { InputType } from './input-type.enum';
import { MonitorTerms } from './monitor-terms.model';
import { QuestionCard } from './question-card.model';
import { QuestionOption } from './question-option.model';
import { CarouselType, MultiChoiceQuestionTypes, QuestionType, RankingTypes, SingleChoiceQuestionTypes } from './question-type.enum';
import { RatingType } from './rating-type.enum';
import { VariableRequiredType } from './source.model';
import { SurveyElementModel } from './survey-element.model';

export class Question extends SurveyElementModel {
  code: string;
  type: QuestionType;
  /** Highest numeric option ID ever assigned to this list; never reduced on deletion. */
  lastOptionIndex?: number;
  pageId: string;
  requiredInd: boolean;
  stimulus: Media[];
  programmerNotes: string;
  neverShow: boolean;
  requireMediaCompletionInd: boolean;
  minViewTimeSeconds: number;
  autoplayMediaInd: boolean;

  // eslint-disable-next-line @typescript-eslint/member-ordering
  @Type(() => QuestionOption)
  options: QuestionOption[];

  //  Info page text
  information: string;

  // Single & Multi choice question types
  useOtherInd: boolean;
  useOtherPosition: number;
  useOtherPinnedInd: boolean;
  useExclusiveOtherInd: boolean;
  otherLabel: string;
  randomizedOptionsInd: boolean;

  // Multi-choice question types & Files upload allowed
  maxLength: number;
  minLength: number;

  // Input
  inputType: InputType;

  // Input & Essay question types
  maxTextLength: number;
  minTextLength: number;

  // Rank Sort
  // eslint-disable-next-line @typescript-eslint/member-ordering
  @Type(() => QuestionOption)
  ranks: QuestionOption[];
  minRank: number;
  feedbackMessage: string;
  useNoneInd: boolean;
  noneLabel: string;

  // Rating
  ratingType: RatingType;
  // // Rating - Numeric
  ratingScale: number;
  minValueText: string;
  maxValueText: string;

  // Carousel
  // eslint-disable-next-line @typescript-eslint/member-ordering
  @Type(() => QuestionCard)
  cards: QuestionCard[];
  randomizedCardsInd: boolean;

  // Heatmap
  imageHeatmap: Media;
  imageHeight: number;
  imageWidth: number;
  imageFit: ImageFit;

  // File Upload
  maxSize: number;
  fileTypes: FileType[];
  allowMultipleInd: boolean;

  // AI Input
  startingQuestion: string;
  promptsNumber: number;
  minPromptsNumber: number;
  discussionGuide: string;
  allowVoiceResponsesInd: boolean;
  aiChatPlaceholderInd?: boolean;

  // Source Variable
  variableToken: string;
  variableRequiredType: VariableRequiredType;

  // Moderation words
  monitorTerms: MonitorTerms;

  // Choice settings
  optionsSettings: ChoiceQuestionSettings;

  get visibleCards(): QuestionCard[] {
    return this.cards
      .filter((card) => card.visible)
      .concat({
        id: `card_${this.cards.length + 1}`,
        text: 'Thank you, please continue on with the rest of the survey'
      } as QuestionCard);
  }

  get visibleOptions(): QuestionOption[] {
    return this.options.filter((option) => option.visible);
  }

  get choices(): QuestionOption[] {
    if (this.type === QuestionType.RankSort) {
      return this.ratingType === RatingType.numeric ? Array.from({ length: this.ratingScale }, (_, index) => this.options[index]) : this.ranks;
    }

    return this.options;
  }

  isEmpty(value: string): boolean {
    if (CarouselType.includes(this.type)) {
      const carouselAnswers = JSON.parse(value) as CarouselTypeResult;
      for (const cardId in carouselAnswers) {
        let anyCardWithValue = false;
        if (Object.prototype.hasOwnProperty.call(carouselAnswers, cardId)) {
          anyCardWithValue = carouselAnswers[cardId].value.length > 0;
        }
        if (anyCardWithValue) {
          return false;
        }
      }
      return true;
    } else if (SingleChoiceQuestionTypes.includes(this.type) || MultiChoiceQuestionTypes.includes(this.type) || RankingTypes.includes(this.type)) {
      const answer = JSON.parse(value) as ChoiceTypeResult;
      return !answer.value || answer.value?.length === 0;
    } else {
      return JSON.parse(value).length === 0;
    }
  }
}
